"use client";

import { talentNetworkText } from "@/i18n/talentNetwork.constants";

import { useState } from "react";
import { Pencil } from "lucide-react";
import { Badge, Button } from "@artha/ui";
import { ProfileEditorPopup } from "../editors/ProfileEditorPopup";
import { ProfileSectionCard } from "../presentation/ProfileSectionCard";
import {
  PROFILE_CARD_ACTION_ICON_CLASS_NAME,
  PROFILE_CARD_CHIP_MAX_LENGTH,
  PROFILE_CARD_EDIT_CLASS_NAME,
  PROFILE_CARD_INPUT_CLASS_NAME,
  PROFILE_CARD_MAX_CHIPS,
  PROFILE_CHIP_CLASS_NAME,
} from "../profileCards.constants";
import { splitProfileChips } from "../profileCards.utils";
import type { ProfileChipSectionProps } from "../profileCards.types";

function ProfileChipEditor({
  kind,
  values,
  onSave,
  onCancel,
}: Pick<ProfileChipSectionProps, "kind" | "values" | "onSave" | "onCancel">) {
  const [chips, setChips] = useState(values);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const singular = talentNetworkText(kind === "skills" ? "skill" : "keyword");
  const add = (save = false) => {
    const next = splitProfileChips([...chips, input].join(","));
    if (
      next.length > PROFILE_CARD_MAX_CHIPS ||
      next.some((value) => value.length > PROFILE_CARD_CHIP_MAX_LENGTH)
    ) {
      setError(
        talentNetworkText("chip_limit", {
          count: PROFILE_CARD_MAX_CHIPS,
          entries: kind,
          length: PROFILE_CARD_CHIP_MAX_LENGTH,
        }),
      );
      return;
    }
    setError("");
    setChips(next);
    setInput("");
    if (save) onSave?.(next);
  };
  return (
    <form
      aria-label={talentNetworkText("edit_entry", { entry: kind })}
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        add(true);
      }}
    >
      <div className="flex flex-wrap gap-2">
        {chips.map((value, index) => (
          <Badge
            key={`${value}-${index}`}
            variant="blue"
            label={value}
            className={PROFILE_CHIP_CLASS_NAME}
            removable
            onRemove={() =>
              setChips(chips.filter((_, itemIndex) => itemIndex !== index))
            }
          />
        ))}
      </div>
      <div className="flex gap-2">
        <input
          aria-label={talentNetworkText("add_entry", { entry: singular })}
          placeholder={talentNetworkText("add_chip", { entry: singular })}
          value={input}
          className={PROFILE_CARD_INPUT_CLASS_NAME}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          className="h-11 rounded-lg"
          onClick={() => add()}
        >
          {talentNetworkText("add")}
        </Button>
      </div>
      <p className="text-xs text-[#667085]">
        {talentNetworkText("chip_help", { entries: kind })}
      </p>
      {error ? (
        <p role="alert" className="text-xs text-[#B42318]">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" className="h-9 rounded-lg text-sm">
          {talentNetworkText("save_entry", { entry: kind })}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9 rounded-lg text-sm"
          onClick={onCancel}
        >
          {talentNetworkText("cancel")}
        </Button>
      </div>
    </form>
  );
}

export function ProfileChipSection(props: ProfileChipSectionProps) {
  const title = talentNetworkText(props.kind);
  return (
    <>
      <ProfileSectionCard
        title={title}
        action={
          props.readOnly ? undefined : (
            <button
              type="button"
              aria-label={talentNetworkText("edit_entry", {
                entry: props.kind,
              })}
              className={PROFILE_CARD_EDIT_CLASS_NAME}
              disabled={props.disabled}
              onClick={props.onEdit}
            >
              <Pencil
                aria-hidden="true"
                className={PROFILE_CARD_ACTION_ICON_CLASS_NAME}
              />
            </button>
          )
        }
      >
        {props.values.length ? (
          <div className="flex flex-wrap gap-2">
            {props.values.map((value, index) => (
              <Badge
                key={`${value}-${index}`}
                variant="blue"
                label={value}
                className={PROFILE_CHIP_CLASS_NAME}
              />
            ))}
          </div>
        ) : (
          <p className="text-xs text-[#667085]">
            {talentNetworkText(props.readOnly ? "no_entries" : "empty_chips", {
              entries: props.kind,
            })}
          </p>
        )}
      </ProfileSectionCard>
      {!props.readOnly && props.onCancel ? (
        <ProfileEditorPopup
          isOpen={props.editing}
          title={talentNetworkText("edit_entry", { entry: props.kind })}
          error={props.error}
          onClose={props.onCancel}
        >
          <ProfileChipEditor {...props} />
        </ProfileEditorPopup>
      ) : null}
    </>
  );
}
